/**
 * Primitives compartilhados pelas páginas CRUD lazy-loaded (Backups, Firmware, Providers,
 * Users, Groups). Mantidos sincronizados com a cópia inline em App.tsx (que está em uso
 * pelas páginas inline VendorsPage, ModelsPage, PopsPage, MapPage). Quando refactor maior
 * for fazer, App.tsx pode também importar daqui — por ora ambos coexistem.
 *
 * Convenção: ao adicionar primitiva nova, exportar daqui E manter cópia em App.tsx em sync.
 */
import { useEffect } from 'react';

export const inp = 'w-full bg-slate-950 ring-1 ring-slate-800 rounded px-3 py-2 text-sm focus:outline-none focus:ring-emerald-600';

export function Section({ title, sub, action, children }: { title: string; sub?: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="bg-slate-900 rounded ring-1 ring-slate-800">
      <header className="px-4 py-3 border-b border-slate-800 flex items-center gap-3">
        <h1 className="font-semibold">{title}</h1>
        {sub && <span className="text-xs text-slate-400">{sub}</span>}
        <div className="ml-auto">{action}</div>
      </header>
      <div className="p-3 overflow-x-auto">{children}</div>
    </section>
  );
}

export function Th({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <th className={`text-left px-3 py-2 font-medium ${className}`}>{children}</th>;
}

export function Td({ children, className = '', onClick }: { children: React.ReactNode; className?: string; onClick?: React.MouseEventHandler<HTMLTableCellElement> }) {
  return <td className={`px-3 py-1.5 align-top ${className}`} onClick={onClick}>{children}</td>;
}

export function PrimaryBtn({ children, onClick, disabled }: { children: React.ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled} className="px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-xs font-semibold">
      {children}
    </button>
  );
}

export function ActionBtn({ children, onClick, variant = 'default', disabled, title }: { children: React.ReactNode; onClick?: () => void; variant?: 'default' | 'danger'; disabled?: boolean; title?: string }) {
  const cls = variant === 'danger' ? 'text-rose-400 hover:bg-rose-500/10' : 'text-slate-300 hover:bg-slate-800';
  return (
    <button onClick={onClick} disabled={disabled} title={title} className={`mr-1 px-2 py-0.5 rounded text-xs ring-1 ring-slate-700 ${cls} disabled:opacity-30 disabled:cursor-not-allowed`}>
      {children}
    </button>
  );
}

export function Modal({ title, children, onClose, wide }: { title: string; children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className={`bg-slate-900 ring-1 ring-slate-800 rounded shadow-2xl w-full ${wide ? 'max-w-3xl' : 'max-w-md'} max-h-[90vh] overflow-y-auto`}>
        <div className="px-4 py-3 border-b border-slate-800 flex items-center">
          <h2 className="font-semibold">{title}</h2>
          <button onClick={onClose} className="ml-auto text-slate-500 hover:text-slate-300 text-lg">×</button>
        </div>
        <div className="p-4 space-y-3">{children}</div>
      </div>
    </div>
  );
}

export function FormRow({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? 'md:col-span-2' : ''}>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

export function confirmDelete(type: string, name: string) {
  return window.confirm(`Tem certeza que quer deletar este ${type}?\n\n${name}`);
}
